import { Popover, Stack, Typography } from "@mui/material";
import {
  BorderStyle,
  MAX_GROUP_CHILD_QUESTIONS,
} from "@pulse/shared-components";
import {
  FoundationColorTokens,
  Shadow,
  Spacing,
  Typography as LeoTypography,
  CornerRadius,
} from "@surya-digital/leo-reactjs-material-ui";
import { Instance } from "mobx-state-tree";
import React, { PropsWithChildren } from "react";
import { QuestionType } from "@pulse/pulse-rpcs/lib/types/questionType";
import { observer } from "mobx-react";
import { ManageQuestionsStore } from "../../store/ManageQuestionsStore";

interface GroupQuestionPopoverProps extends PropsWithChildren {
  anchorElement: HTMLButtonElement | null;
  setAnchorElement: React.Dispatch<
    React.SetStateAction<HTMLButtonElement | null>
  >;
  manageQuestionsStore: Instance<typeof ManageQuestionsStore>;
  spacing: Spacing;
  border: BorderStyle;
  cornerRadius: CornerRadius;
  tokens: FoundationColorTokens<string>;
  shadow: Shadow;
  typography: LeoTypography;
  selectedChildQuestionId: string | undefined;
  setSelectedChildQuestionId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}

export const GroupQuestionPopover = observer(
  ({
    anchorElement,
    setAnchorElement,
    manageQuestionsStore,
    spacing,
    border,
    cornerRadius,
    tokens,
    shadow,
    typography,
    selectedChildQuestionId,
    setSelectedChildQuestionId,
  }: GroupQuestionPopoverProps): React.ReactElement => {
    const GroupQuestions = observer((): React.ReactElement => {
      return (
        <Stack>
          {manageQuestionsStore.manageQuestionsList.map((question, index) => {
            if (
              question.surveyQuestionDetails.questionType ===
                QuestionType.QuestionType.GROUP &&
              question.surveyQuestionDetails.childQuestionIds.length <
                MAX_GROUP_CHILD_QUESTIONS
            ) {
              return (
                <Stack
                  key={index}
                  padding={`${spacing.spaceSM} ${spacing.spaceMD}`}
                  gap={spacing.spaceXS}
                  onClick={(): void => {
                    if (selectedChildQuestionId) {
                      manageQuestionsStore.moveIntoGroupQuestion(
                        question.surveyQuestionDetails.questionId,
                        selectedChildQuestionId,
                      );
                      setSelectedChildQuestionId(undefined);
                      setAnchorElement(null);
                    }
                  }}
                  sx={{
                    ":hover": {
                      backgroundColor: tokens.backgroundHovered,
                    },
                    cursor: "pointer",
                    width: "100%",
                  }}
                >
                  <Typography {...typography.b1} color={tokens.label}>
                    {question.surveyQuestionDetails.questionCode}
                  </Typography>
                </Stack>
              );
            }
          })}
        </Stack>
      );
    });

    return (
      <Popover
        open={anchorElement !== null}
        anchorEl={anchorElement}
        onClose={(): void => {
          setAnchorElement(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
        sx={{
          "& .MuiPopover-paper": {
            width: "360px",
            boxShadow: shadow.level3,
            border: border.subtle,
            borderRadius: cornerRadius.radiusXS,
            padding: `${spacing.spaceXS} 0`,
          },
        }}
      >
        <GroupQuestions />
      </Popover>
    );
  },
);
